import { Component } from 'react';
import PropTypes from 'prop-types';
import flow from 'lodash/flow';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import get from 'lodash/get';
import find from 'lodash/find';
import { bindActionCreators } from 'redux';
import filter from 'lodash/filter';
import classNames from 'clsx';

import tracking from 'src/common/constants/payment/tracking';
import subscribe from 'src/common/constants/analytics/categoryActionLabel/subscribe';
import { selectIsSubscriptionEnabled } from 'src/common/selectors/config';
import SubscribePaymentForm from '../payment/SubscribePaymentForm';
import { hasPairAuthFlowParam } from '../../utils/auth/pairAuthFlowHelpers';
import { fbTrack, fbPixelCustomEvents } from '../../utils/fbTrack';
import { logSubscribeActivity, logClientError } from '../../actions/logging';
import { trackSubscriptionPage } from '../../actions/pageTracking';
import withAuthHandling from '../../decorators/auth/withAuthHandling';
import unauthRedirectHandler from '../../utils/auth/unauthRedirectHandler';
import generateOriginLabelFromQuery from '../../utils/generateOriginLabelFromQuery';

import {
  PROFILE_QUERY_PARAM,
  BROWSE_QUERY_PARAM,
} from '../../constants/queryParams';

import css from '../payment/payment.module.scss';
import PaymentFlowFormLayout from '../payment/PaymentFlowFormLayout';
import { SUBSCRIBE_GO_PREMIUM_NOW_LABEL } from '../../constants/localizations/subscription';
import hasValidCouponData from '../../utils/subscription/hasValidCouponData';
import isCouponFlow from '../../utils/subscription/isCouponFlow';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import SubscriptionServiceUnavailable from './form/SubscriptionServiceUnavailable';
import PremiumNotAvailable from './PremiumNotAvailable';

function getGuideIdFromQuery(location) {
  return (
    get(location, ['query', PROFILE_QUERY_PARAM]) ||
    get(location, ['query', BROWSE_QUERY_PARAM])
  );
}

function getSourceWithoutGuideId(location) {
  const querySource = generateOriginLabelFromQuery(location, true);
  return querySource
    ? `${subscribe.labels.payment}.${querySource}`
    : subscribe.labels.payment;
}

export class Subscribe extends Component {
  static propTypes = {
    products: PropTypes.array.isRequired,
    actions: PropTypes.object.isRequired,
    isSubscriptionEnabled: PropTypes.bool.isRequired,
    breakpoint: PropTypes.number.isRequired,
    hasValidCouponInfo: PropTypes.bool,
    coupon: PropTypes.object,
    history: PropTypes.object.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  state = {
    couldNotFetchProducts: false,
  };

  componentDidMount() {
    const { actions, isSubscriptionEnabled, products } = this.props;
    const { location } = this.context;

    if (isSubscriptionEnabled) {
      fbTrack(fbPixelCustomEvents.PaymentPageVisit);

      if (products.length <= 0) {
        this.handleGetProductsBySkusError();
        return;
      }

      actions.logSubscribeActivity(
        subscribe.actions.show,
        getSourceWithoutGuideId(location),
        getGuideIdFromQuery(location),
      );
      actions.trackSubscriptionPage();
    }
  }

  handleGetProductsBySkusError = () => {
    this.setState({ couldNotFetchProducts: true });
    this.props.actions.logClientError({
      message: tracking.labels.productLoadFailed,
      context: {
        source: 'subscribe',
      },
    });
  };

  render() {
    const {
      isSubscriptionEnabled,
      hasValidCouponInfo,
      coupon,
      breakpoint,
      products,
      history,
    } = this.props;
    const { couldNotFetchProducts } = this.state;
    const { location } = this.context;

    const isPairingFlow = hasPairAuthFlowParam(location?.query);
    const hasValidCoupon = hasValidCouponInfo && isCouponFlow(location);
    const couponData = hasValidCoupon ? coupon : {};

    if (!isSubscriptionEnabled) {
      return (
        <div className={css.container}>
          <Helmet>
            <title>TuneIn Premium Free Trial</title>
          </Helmet>
          <div className={css.topSection}>
            <div className={css.containerRestrictions}>
              <div className="row">
                <div className="col-xs-12">
                  <PremiumNotAvailable
                    headerClassName={css.title}
                    history={history}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const layoutProps = {
      isPaymentFlow: true,
      isPairingFlow,
      alwaysShowUpsellTable: false,
    };

    if (couldNotFetchProducts) {
      return (
        <PaymentFlowFormLayout {...layoutProps}>
          <SubscriptionServiceUnavailable isPairingFlow={isPairingFlow} />
        </PaymentFlowFormLayout>
      );
    }

    if (products.length === 0 || find(products, (p) => p.isFetching)) {
      return null; // TODO: load error state and loading state
    }

    return (
      <div
        className={classNames(css.container, {
          [css.pairingFlow]: isPairingFlow,
        })}
      >
        <Helmet title={'TuneIn Premium Free Trial'} />
        <SubscribePaymentForm
          breakpoint={breakpoint}
          products={products}
          location={location}
          hasValidCoupon={hasValidCoupon}
          couponData={couponData}
          defaultSubmitButtonLabel={SUBSCRIBE_GO_PREMIUM_NOW_LABEL}
          paymentFlowLayoutProps={layoutProps}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    subscription: { coupon },
    products,
  } = state;

  return {
    products: filter(
      products,
      (product) => !product.isRenew && !product.hasFailed,
    ),
    isSubscriptionEnabled: selectIsSubscriptionEnabled(state),
    coupon,
    hasValidCouponInfo: hasValidCouponData(state),
  };
}

function mapDispatchToProps(dispatch) {
  const actions = {
    logClientError,
    logSubscribeActivity,
    trackSubscriptionPage,
  };

  return {
    actions: bindActionCreators(actions, dispatch),
  };
}

export default flow(
  connect(mapStateToProps, mapDispatchToProps),
  withAuthHandling({ unauthRedirectHandler }),
)(Subscribe);
